import React, { useCallback, useEffect, useState } from 'react';
import { ADMIN } from '@/lib/adminApi';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/context/AdminAuthContext';

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', office: 'Office' };
const IS_DEV = process.env.NODE_ENV === 'development';

export default function AdminTeam() {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'admin', password: '' });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ADMIN.team();
      setRows(r.members || []);
    } catch (err) {
      toast.error('Failed to load team');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminTeam reload failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const invite = useCallback(async () => {
    if (!form.name || !form.email || !form.password) { toast.error('All fields required'); return; }
    if (form.password.length < 8) { toast.error('Password must be 8+ characters'); return; }
    try {
      await ADMIN.inviteTeam(form);
      toast.success('Team member added');
      setOpen(false);
      setForm({ name: '', email: '', role: 'admin', password: '' });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }, [form, reload]);

  const remove = useCallback(async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await ADMIN.deleteTeam(id);
      toast.success('Removed');
      reload();
    } catch (err) {
      toast.error('Failed');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminTeam delete failed', err);
      }
    }
  }, [reload]);

  const setActive = useCallback(async (id, active) => {
    try {
      await ADMIN.updateTeam(id, { active });
      reload();
    } catch (err) {
      toast.error('Failed');
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.error('AdminTeam set-active failed', err);
      }
    }
  }, [reload]);

  const setRole = async (id, role) => {
    try { await ADMIN.updateTeam(id, { role }); reload(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-team-view">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Team Members</h1>
          <p className="text-sm text-slate-500 mt-1">Only Owners can manage team access.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#0A2540] hover:bg-[#13294a]" data-testid="team-invite-button"><UserPlus className="h-4 w-4 mr-2" /> Invite Member</Button>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <Skeleton className="h-48" /> : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Last Login</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{m.name}</td>
                    <td className="px-4 py-3 text-slate-700">{m.email}</td>
                    <td className="px-4 py-3">
                      <Select value={m.role} onValueChange={(v) => setRole(m.id, v)} disabled={m.id === user?.id}>
                        <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{m.last_login_at ? new Date(m.last_login_at).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setActive(m.id, !m.active)} disabled={m.id === user?.id} className={`px-2 py-1 rounded-md text-xs font-semibold ${m.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'} disabled:opacity-50`}>{m.active ? 'Active' : 'Inactive'}</button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => remove(m.id)} disabled={m.id === user?.id} className="h-8 w-8 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`team-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-form-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-form-email" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Temporary Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" data-testid="team-form-password" /></div>
            <p className="text-xs text-slate-500">Member can change their password from Settings after first login.</p>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={invite} className="bg-[#0A2540] hover:bg-[#13294a]" data-testid="team-form-save">Add Member</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
